import { Modal, Group, Box, Text, LoadingOverlay } from "@mantine/core";
import { Alert, Button } from "@xplatform/design-system";
import {
    IconChevronRight,
    IconChevronLeft,
    IconCheck,
} from "@tabler/icons-react";
import { useFlowConfig } from "@hooks/flow";
import { useFlowWizard } from "@hooks/flow/useFlowWizard";
import { useTranslation } from "@hooks/useTranslation";
import type { FlowConfigViewModel } from "@models/doctype";
import {
    buildCreateFlowConfigRequest,
    type FlowConfigFormData,
} from "@utils/builders/flowConfig";
import { FlowTypeSelector } from "./FlowTypeSelector";
import { FlowStepStepper } from "./FlowStepStepper";
import { StepConfiguration } from "./StepConfiguration";
import { PreprocessingOptionsFields } from "../PreprocessingOptionsFields";
import "./CreateFlowConfigDialog.scss";

interface CreateFlowConfigDialogProps {
    open: boolean;
    onClose: () => void;
    /** Called with the version the dialog created. */
    onSuccess?: (config: FlowConfigViewModel) => void;
    docTypeId: string;
    existingConfig?: FlowConfigViewModel | null;
}

export const CreateFlowConfigDialog = ({
    open,
    onClose,
    onSuccess,
    docTypeId,
    existingConfig,
}: CreateFlowConfigDialogProps) => {
    const { t } = useTranslation();
    const { createFlowConfig, isLoading, error, clearError } = useFlowConfig();

    const {
        activeStep,
        activeTab,
        flowType,
        flowDescription,
        preprocessingOptions,
        steps,
        isFlowTypeSelected,
        providers,
        models,
        selectedProvider,
        isLoadingModels,
        isLoadingProviders,
        setActiveTab,
        setFlowType,
        setFlowDescription,
        setPreprocessingOptions,
        setIsFlowTypeSelected,
        handleProviderChange,
        handleModelChange,
        updateCurrentStepConfig,
        updateCurrentStepRuntimeOptions,
        isStepValid,
        canProceed,
        canSubmit,
        handleNext,
        handleBack,
        updateAgents,
    } = useFlowWizard({ open, existingConfig });

    const handleSubmit = async () => {
        if (!canSubmit()) return;

        const formData: FlowConfigFormData = {
            flowBaseId: existingConfig?.flowBaseId || null,
            flowType,
            flowDescription: flowDescription || null,
            steps,
            preprocessingOptions,
        };

        const request = buildCreateFlowConfigRequest(formData);
        const result = await createFlowConfig(docTypeId, request);

        if (result) {
            clearError();
            onClose();
            onSuccess?.(result);
        }
    };

    const currentStep = steps[activeStep];

    return (
        <Modal
            opened={open}
            onClose={onClose}
            title={
                <Text fw={600} size="lg">
                    {existingConfig
                        ? t("admin.flowConfigs.create.newVersionTitle", { version: existingConfig.version })
                        : t("admin.flowConfigs.create.newFlowTitle")}
                </Text>
            }
            size="90%"
            className="create-flow-config-dialog"
            closeButtonProps={{ disabled: isLoading }}
            portalProps={{ target: "#admin-portal-root" }}
            styles={{
                content: {
                    maxHeight: "calc(100vh - 60px)",
                    display: "flex",
                    flexDirection: "column",
                },
                header: {
                    flexShrink: 0,
                },
                body: {
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    overflow: "hidden",
                },
            }}
        >
            <LoadingOverlay visible={isLoading} />
            {error && (
                <Alert color="red" mb="md" withCloseButton onClose={clearError}>
                    {error}
                </Alert>
            )}

            <Box className="create-flow-config-dialog__content">
                {!existingConfig && !isFlowTypeSelected && (
                    <FlowTypeSelector
                        flowType={flowType}
                        flowDescription={flowDescription}
                        onFlowTypeChange={setFlowType}
                        onDescriptionChange={setFlowDescription}
                    />
                )}

                {isFlowTypeSelected && steps.length > 0 && (
                    <>
                        {activeStep === 0 && (
                            <Box mb="lg">
                                <Text fw={600} mb="sm">{t("admin.flowConfigs.create.flowRuntimeOptions")}</Text>
                                <PreprocessingOptionsFields
                                    value={preprocessingOptions}
                                    onChange={setPreprocessingOptions}
                                />
                            </Box>
                        )}
                        <FlowStepStepper
                            steps={steps}
                            activeStep={activeStep}
                            isStepValid={isStepValid}
                        />

                        {currentStep && (
                            <StepConfiguration
                                step={currentStep}
                                stepNumber={activeStep + 1}
                                activeTab={activeTab}
                                providers={providers}
                                models={models}
                                selectedProvider={selectedProvider}
                                isLoadingModels={isLoadingModels}
                                isLoadingProviders={isLoadingProviders}
                                onTabChange={setActiveTab}
                                onProviderChange={handleProviderChange}
                                onModelChange={handleModelChange}
                                onFieldChange={updateCurrentStepConfig}
                                onRuntimeOptionsChange={updateCurrentStepRuntimeOptions}
                                onAgentsChange={updateAgents}
                            />
                        )}
                    </>
                )}
            </Box>

            <Group
                className="create-flow-config-dialog__actions"
                justify="space-between"
                mt="md"
            >
                <Button variant="subtle" onClick={onClose} disabled={isLoading}>
                    {t("common.cancel")}
                </Button>
                <Group gap="sm">
                    {!isFlowTypeSelected && (
                        <Button
                            onClick={() => {
                                if (flowType) {
                                    setIsFlowTypeSelected(true);
                                }
                            }}
                            disabled={!flowType || isLoading}
                            rightSection={<IconChevronRight size={16} />}
                        >
                            {t("admin.flowConfigs.create.continue")}
                        </Button>
                    )}

                    {isFlowTypeSelected && (
                        <>
                            <Button
                                variant="default"
                                onClick={() => {
                                    if (activeStep === 0) {
                                        setIsFlowTypeSelected(false);
                                    } else {
                                        handleBack();
                                    }
                                }}
                                disabled={isLoading}
                                leftSection={<IconChevronLeft size={16} />}
                            >
                                {t("admin.flowConfigs.create.back")}
                            </Button>
                            {activeStep < steps.length - 1 ? (
                                <Button
                                    onClick={handleNext}
                                    disabled={!canProceed() || isLoading}
                                    rightSection={<IconChevronRight size={16} />}
                                >
                                    {t("admin.flowConfigs.create.next")}
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleSubmit}
                                    disabled={!canSubmit() || isLoading}
                                    leftSection={<IconCheck size={16} />}
                                >
                                    {t("common.save")}
                                </Button>
                            )}
                        </>
                    )}
                </Group>
            </Group>
        </Modal>
    );
};
